import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import HeroSection from "../../components/HeroSection";
import groupEventAsset from "../../assets/gallery/events/groupix.png";
import { eventAPI, resolveMediaUrl } from "../../services/api";

export default function EventDetail() {
  const groupEvent = `${groupEventAsset}?v=20260424`;
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadEvent = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await eventAPI.getById(id);
        setEvent(response?.event || null);
      } catch (err) {
        setError(err?.message || "Failed to load event details");
      } finally {
        setLoading(false);
      }
    };
    loadEvent();
  }, [id]);

  const paragraphs = useMemo(() => {
    const source = event?.content || event?.description || "";
    return source
      .split(/\n{2,}/)
      .map((segment) => segment.trim())
      .filter(Boolean);
  }, [event]);

  if (loading) {
    return (
      <div className="section text-center">
        <p className="text-gray-600">Loading event details...</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="section text-center">
        <p className="text-red-600 mb-4">{error || "Event not found"}</p>
        <Link to="/resources/news-events" className="text-primary-600 font-semibold">
          Back to News & Events
        </Link>
      </div>
    );
  }

  const bannerImage = resolveMediaUrl(event.banner_url || event.media_url) || groupEvent;
  const isVideoFile = (url = "") => /\.(mp4|webm|mov|avi|mkv)(\?.*)?$/i.test(url);

  return (
    <div className="w-full bg-white">
      <HeroSection
        title={event.title}
        subtitle={event.description}
        heroImage={bannerImage}
        buttons={[{ text: "Back to News & Events", link: "/resources/news-events", variant: "blue" }]}
      />

      <section className="section bg-white">
        <div className="section-container max-w-4xl">
          <div className="mb-6">
            <Link to="/resources/news-events" className="inline-flex items-center gap-2 text-primary-600 font-semibold">
              <FaArrowLeft />
              Back to all events
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-8">
            <span className="inline-flex items-center gap-2">
              <FaCalendarAlt />
              {new Date(event.event_date).toLocaleDateString()}
            </span>
            {event.event_location && (
              <span className="inline-flex items-center gap-2">
                <FaMapMarkerAlt />
                {event.event_location}
              </span>
            )}
          </div>

          <div className="space-y-5 text-gray-700 leading-relaxed">
            {(paragraphs.length ? paragraphs : [event.description]).map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          {Array.isArray(event.gallery_images) && event.gallery_images.length > 0 && (
            <div className="mt-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-5">Event Gallery</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {event.gallery_images.map((image, index) => (
                  isVideoFile(image) ? (
                    <video
                      key={`${image}-${index}`}
                      src={resolveMediaUrl(image)}
                      controls
                      className="w-full h-64 rounded-lg border border-gray-200 bg-black"
                      style={{ objectFit: "contain" }}
                    />
                  ) : (
                    <img
                      key={`${image}-${index}`}
                      src={resolveMediaUrl(image)}
                      alt={`${event.title} gallery ${index + 1}`}
                      className="w-full h-64 object-cover rounded-lg border border-gray-200"
                    />
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
